import type { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import { graphScopes } from "./authConfig";

// Refresh a little early so a token can't expire in flight or between the
// client check and the server's own exp validation.
const EXPIRY_BUFFER_SECONDS = 120;

function expiresSoon(idToken: string | undefined): boolean {
  if (!idToken) return true;
  try {
    const payload = idToken.split(".")[1];
    const { exp } = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof exp !== "number" || exp - Date.now() / 1000 < EXPIRY_BUFFER_SECONDS;
  } catch {
    return true;
  }
}

/**
 * Entra ID tokens last about an hour, but acquireTokenSilent can serve a
 * cached ID token past that as long as the Graph access token it was issued
 * with is still valid — the API then rejects it as expired. Check the ID
 * token's own exp and force a round-trip when it's stale.
 */
export async function getFreshIdToken(
  instance: IPublicClientApplication,
  account: AccountInfo
): Promise<string> {
  const cached = await instance.acquireTokenSilent({ account, scopes: graphScopes });
  if (!expiresSoon(cached.idToken)) return cached.idToken;

  const refreshed = await instance.acquireTokenSilent({
    account,
    scopes: graphScopes,
    forceRefresh: true,
  });
  return refreshed.idToken;
}
